<template>
  <section
    v-if="firstFewProfessionTasks?.length"
    class="ProfessionPageSectionProfessionTask"
  >
    <BlockHeading
      :title-level="2"
      :title="$t('professionalsPage.mostPopularTasks.title')"
      :paragraph="$t('professionalsPage.mostPopularTasks.titleParagraph')"
    />

    <div class="browseBlockBase">
      <div class="contentHeader">
        <div class="ptTabs">
          <div
            v-for="(professionTask, index) in firstFewProfessionTasks"
            :key="index"
            class="ptTab"
            :class="{
              active: index === selectedProfessionTaskIndex,
            }"
            @click="changeProfessionTask(professionTask)"
          >
            {{ professionTask.name }}
          </div>
        </div>
        <div class="search">
          <AutocompleteProfessionTask
            :placeholder="$t('professionTaskSelector.placeholder')"
            :profession-id="profession.id"
            @input="changeProfessionTask"
          />
        </div>
      </div>

      <NuxtLink
        v-if="selectedProfessionTask"
        v-slot="{ href, navigate }"
        :to="
          localePath({
            name: 'prices',
            params: {
              professionTask: selectedProfessionTask.seoName,
            },
          })
        "
      >
        <div class="contentBody">
          <JoszakiHeading :level="3">
            <a :href="href" class="text-inherit" @click="navigate">
              {{
                $t("professionalsPage.mostPopularTasks.somePrices", {
                  professionTaskName: selectedProfessionTask.name,
                })
              }}
            </a>
          </JoszakiHeading>
          <img
            v-if="loading"
            v-lazy-load data-src="~/assets/img/loader.svg"
            width="100"
            class="peopleListLoader"
          />

          <PeopleSwiper
            v-if="peopleByProfessionTask?.length > 0"
            :class="{ loadingLook: loading }"
            class="person-swiper"
            @click.native="navigate"
          >
            <PeopleSwiperProfessionTaskPersonInset
              v-for="person in peopleByProfessionTask"
              :key="person.id"
              :profession-task="selectedProfessionTask"
              :person="person"
            />
          </PeopleSwiper>

          <PeopleSwiperEmpty v-else>
            <i18n path="peopleSwiper.emptyProfessionTask">
              <template #professionTaskName>
                <span class="font-bold">{{ selectedProfessionTask.name }}</span>
              </template>
            </i18n>
          </PeopleSwiperEmpty>

          <JoszakiButton
            type="info"
            :to="
              localePath({
                name: 'prices',
                params: {
                  professionTask: selectedProfessionTask.seoName,
                },
              })
            "
          >
            {{
              $t("professionalsPage.showAllPrices", {
                professionTaskName: selectedProfessionTask.name,
              })
            }}
          </JoszakiButton>
        </div>
      </NuxtLink>
    </div>
  </section>
</template>

<script>
import { inject } from "@nuxtjs/composition-api";
import { EVENTS } from "~/pages/professionals/_profession/events";
import AutocompleteProfessionTask from "~/components/_refactored/autocomplete/AutocompleteProfessionTask.vue";
import PeopleSwiper from "~/components/swiper/PeopleSwiper.vue";
import PeopleSwiperProfessionTaskPersonInset from "~/components/swiper/PeopleSwiperProfessionTaskPersonInset.vue";
import PeopleSwiperEmpty from "~/components/swiper/PeopleSwiperEmpty.vue";

export default {
  components: {
    AutocompleteProfessionTask,
    PeopleSwiper,
    PeopleSwiperProfessionTaskPersonInset,
    PeopleSwiperEmpty,
  },
  setup() {
    return {
      peopleByProfessionTask: inject("peopleByProfessionTask"),
      loading: inject("loading"),
      selectedProfessionTask: inject("selectedProfessionTask"),
      professionTasks: inject("professionTasks"),
      profession: inject("profession"),
    };
  },
  computed: {
    firstFewProfessionTasks() {
      return (
        this.professionTasks?.slice(
          0,
          Math.min(this.professionTasks?.length, 5)
        ) ?? []
      );
    },
    selectedProfessionTaskIndex() {
      return this.firstFewProfessionTasks?.findIndex(
        (professionTask) =>
          professionTask.seoName === this.selectedProfessionTask.seoName
      );
    },
  },
  methods: {
    changeProfessionTask(professionTask) {
      this.$mitt.emit(
        EVENTS.PROFESSIONS_PAGE_CHANGE_PROFESSION_TASK,
        professionTask
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@use "styles/_all-members" as global;
$bodyBg: rgba(64, 79, 91, 0.05);

.contentHeader {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  @include global.touch {
    flex-direction: column;
  }

  .ptTabs {
    display: flex;
    align-items: center;
    user-select: none;
    max-width: calc(100% - 300px);
    overflow-x: auto;
    @include global.touch {
      max-width: unset;
      width: 100%;
    }

    .ptTab {
      padding: 15px 20px;
      font-weight: 600;
      font-size: 16px;
      cursor: pointer;
      white-space: nowrap;
      border-radius: 12px 12px 0px 0px;

      &::first-letter {
        text-transform: capitalize;
      }

      &.active {
        color: global.$primary;
        font-weight: 700;
        background: $bodyBg;
      }
    }
  }

  .cityButtons {
    display: flex;
    align-items: center;
    user-select: none;
    background: $bodyBg;
    border-radius: 12px 12px 0px 0px;
    padding: 15px 15px;
    padding-bottom: 10px;
    max-width: calc(100% - 300px);
    overflow-x: auto;
    @include global.touch {
      max-width: unset;
      width: 100%;
    }

    .cityButton {
      padding: 15px 20px;
      padding-bottom: 10px;
      font-weight: 600;
      font-size: 16px;
      cursor: pointer;
      white-space: nowrap;
      border-radius: 12px 12px 0px 0px;
      font-size: 14px;
      padding: 5px 15px;
      background: white;
      border-radius: 3px;
      margin-right: 10px;

      &:last-of-type {
        margin-right: 0px;
      }

      &::first-letter {
        text-transform: capitalize;
      }

      &.active {
        font-weight: 700;
        background: global.$primary;
        color: white;
      }
    }
  }

  .search {
    padding: 15px;
    padding-bottom: 0px;
    background: $bodyBg;
    border-radius: 12px 12px 0px 0px;
    @include global.touch {
      width: 100%;
      border-radius: 0px;
    }
  }
}

.contentBody {
  border-radius: 0px 0px 13px 13px;
  width: 100%;
  padding: 15px;
  position: relative;
  background: $bodyBg;
}
</style>
